import { getApiUrl, getAuthHeaders } from './api-client';

/**
 * GET /admin/programs
 * Получить список всех программ
 */
export async function getAdminPrograms(params = {}) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return { items: [], total: 0 };
        }

        const queryParams = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== '') {
                const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                queryParams.append(key, stringValue);
            }
        }

        const queryString = queryParams.toString();
        const url = `${apiUrl}/admin/programs${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            }
        });

        if (!response.ok) {
            console.error(`Failed to fetch programs. Status: ${response.status}`);
            return { items: [], total: 0 };
        }

        const data = await response.json();
        return {
            items: data.items || [],
            total: data.total || 0,
        };
    } catch (error) {
        console.error("Error fetching admin programs:", error);
        return { items: [], total: 0 };
    }
}

/**
 * POST /admin/programs
 * Создать новую программу
 */
export async function createAdminProgram(programData) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        const normalizeField = (value) => {
            if (!value) return { ua: '', en: '' };
            if (typeof value === 'object') return value;
            return { ua: value, en: value };
        };

        const payload = {
            id: programData.id,
            slug: programData.slug,
            ageLabel: normalizeField(programData.ageLabel),
            title: normalizeField(programData.title),
            summary: normalizeField(programData.summary),
            ctaLabel: normalizeField(programData.ctaLabel),
            coverImageUrl: programData.coverImageUrl || '',
            pagePath: programData.pagePath || '',
            page: programData.page || {},
            sortOrder: programData.sortOrder || 0,
            isFeatured: programData.isFeatured || false,
            isActive: programData.isActive !== false,
        };

        const response = await fetch(`${apiUrl}/admin/programs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to create program. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error creating admin program:", error);
        return { error: true, message: error.message };
    }
}

/**
 * GET /admin/programs/{programId}
 * Получить программу по ID
 */
export async function getAdminProgramById(programId, params = {}) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            throw new Error('API URL is not configured');
        }

        const queryParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        }

        const queryString = queryParams.toString();
        const url = `${apiUrl}/admin/programs/${programId}${queryString ? `?${queryString}` : ''}`;


        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            }
        });

        const data = await response.json();

        if (!response.ok) {
            const errorMsg = data?.message || `Failed to fetch program. Status: ${response.status}`;
            console.error(`Error fetching program by id:`, errorMsg, data);
            throw new Error(errorMsg);
        }

        return data;
    } catch (error) {
        console.error("Error fetching admin program by id:", error);
        throw error;
    }
}

/**
 * PUT /admin/programs/{programId}
 * Обновить программу
 */
export async function updateAdminProgram(programId, programData) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        const normalizeField = (value) => {
            if (!value) return { ua: '', en: '' };
            if (typeof value === 'object') return value;
            return { ua: value, en: value };
        };

        const payload = {
            id: programData.id,
            slug: programData.slug,
            ageLabel: normalizeField(programData.ageLabel),
            title: normalizeField(programData.title),
            summary: normalizeField(programData.summary),
            coverImageUrl: programData.coverImageUrl || '',
            isFeatured: programData.isFeatured || false,
            isActive: programData.isActive !== false,
        };

        // Не отправляем поля, которых нет в данных формы, чтобы PUT
        // не затирал существующий контент страницы пустыми значениями
        if (programData.ctaLabel !== undefined) payload.ctaLabel = normalizeField(programData.ctaLabel);
        if (programData.pagePath !== undefined) payload.pagePath = programData.pagePath;
        if (programData.page !== undefined) payload.page = programData.page;
        if (programData.sortOrder !== undefined) payload.sortOrder = programData.sortOrder;

        const response = await fetch(`${apiUrl}/admin/programs/${programId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to update program. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error updating admin program:", error);
        return { error: true, message: error.message };
    }
}

/**
 * DELETE /admin/programs/{programId}
 * Удалить программу
 */
export async function deleteAdminProgram(programId) {
    try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
            return null;
        }

        const response = await fetch(`${apiUrl}/admin/programs/${programId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders(),
            },
        });

        if (response.status === 204) {
            return { ok: true };
        }

        const data = await response.json();

        if (!response.ok) {
            console.error(`Failed to delete program. Status: ${response.status}`, data);
            return { error: true, ...data };
        }

        return data;
    } catch (error) {
        console.error("Error deleting admin program:", error);
        return { error: true, message: error.message };
    }
}
